import {
  allVisionConditions,
  ApplicationFormData,
  useApplication,
  useUpdateApplication,
} from "@/api/application/application";
import { useLoggedInUser } from "@/api/user/user";
import { BoolRadioCards, RadioCards } from "@/components/UI/forms/RadioCards";
import { FieldController } from "@/components/UI/forms/FieldController";
import { InputCheckbox } from "@/components/UI/forms/InputCheckbox";
import { InputTextArea } from "@/components/UI/forms/InputTextArea";
import { VisualAccuity } from "@/components/UI/forms/VisualAccuity";
import { booleanValidator } from "@/utils/formUtils";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Center,
  Flex,
  Heading,
  Grid,
  Link,
  Stack,
  Text,
  VStack,
  useToast,
  Spinner,
  Collapse,
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { defaultRegistryFormData, RegistryFormData } from "..";
import { medicalCompletionErrors } from "../validation";
import ErrorSummary from "../ErrorSummary";
import { useTranslation, Trans } from "react-i18next";
import { LocalizedLink } from "@/components/i18n/LocalizedLink";
import { useLocalizedNavigate } from "@/hooks/useLocalizedNavigate";

export const MedicalPage = () => {
  const { data: applicationData, isLoading } = useApplication();
  const { data: userData } = useLoggedInUser();
  const { mutateAsync: updateApplication } = useUpdateApplication();

  const toast = useToast();
  const localizedNavigate = useLocalizedNavigate();
  const { t } = useTranslation(["registry", "common", "validation"]);

  const formHook = useForm<RegistryFormData>({
    defaultValues: defaultRegistryFormData,
    mode: "all",
    criteriaMode: "all",
  });

  const formControl = formHook.control;
  const formData = useWatch({
    control: formHook.control,
  });
  const [hydratedFromApplicationId, setHydratedFromApplicationId] = useState<string | null>(null);

  useEffect(() => {
    if (!applicationData) return;
    const latest = formHook.getValues();
    formHook.reset({
      ...latest,
      application: {
        ...latest.application,
        ...applicationData,
      },
    });
    setHydratedFromApplicationId(applicationData.id);
  }, [applicationData, formHook]);

  const selfSubmit = (applicationData?.applicant_type || "self") === "self";

  // Key suffix for self/proxy translation variants
  const pronounKey = selfSubmit ? "self" : "proxy";

  const hasMacularDegeneration = formData.application?.vision_conditions?.includes(
    allVisionConditions.AGE_RELATED_MACULAR_DEGENERATION,
  );
  const hasOtherVisionConditions = formData.application?.vision_conditions?.includes(
    allVisionConditions.OTHER,
  );

  const knowsVisualAcuity = useWatch({
    control: formHook.control,
    name: "application.vision_acuity_known",
  });

  const medicalErrors = useMemo(() => {
    if (!formData.application) return [];
    return medicalCompletionErrors(formData.application);
  }, [formData.application]);

  const medicalIsComplete = useMemo(() => {
    return !medicalErrors || medicalErrors.length === 0;
  }, [medicalErrors]);

  const visionErrors = useMemo(() => {
    const errors = formHook.formState.errors.application;
    if (!errors) return [];

    const baseFields: (keyof ApplicationFormData)[] = [
      "vision_conditions",
      "vision_macular_degeneration_type",
      "vision_other_details",
      "vision_acuity_known",
      "vision_acuity_left",
      "vision_acuity_right",
      "vision_can_read_normal_print",
      "vision_can_read_headlines",
      "vision_can_see_indoors",
      "vision_can_localize_person",
      "vision_driving",
    ];

    return baseFields.flatMap((field) => {
      const msg = errors[field]?.message;
      return msg ? [msg as string] : [];
    });
  }, [formHook.formState.errors.application]);

  const isHydrated = !!applicationData?.id && hydratedFromApplicationId === applicationData.id;
  const canEditMedical =
    applicationData && applicationData.consent_locked && applicationData.consent_health_collection;
  const applicationSubmitted = applicationData?.is_submitted;

  const saveAndContinue = useCallback(async () => {
    if (!userData?.user?.id) return;
    if (!formData.application?.id) return;

    const valid = await formHook.trigger();
    if (!valid) return;

    const payload: ApplicationFormData = {
      ...formHook.getValues().application,
      user_id: userData.user.id,
      is_submitted: applicationData?.is_submitted ?? false,
    };

    try {
      await updateApplication({ id: payload.id!, ...payload });
      toast({
        title: t("common:toast.saved"),
        description: t("registry:medical.medicalSaved"),
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      if (applicationSubmitted) {
        localizedNavigate("/");
      } else {
        localizedNavigate("/registry/confirm");
      }

      window.scrollTo(0, 0);
      return;
    } catch (error) {
      const message = error instanceof Error ? error.message : t("common:errors.unknownError");
      toast({
        title: t("common:toast.error"),
        description: message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [
    formData.application?.id,
    formHook,
    localizedNavigate,
    toast,
    updateApplication,
    userData?.user?.id,
    applicationData,
    applicationSubmitted,
    t,
  ]);

  const handleContinue = useCallback(async () => {
    // If consent isn't complete, allow users to continue exploring the flow without saving.
    if (!canEditMedical) {
      localizedNavigate("/registry/confirm");
      window.scrollTo(0, 0);
      return;
    }
    await saveAndContinue();
  }, [canEditMedical, localizedNavigate, saveAndContinue]);

  if (isLoading) {
    return (
      <Center paddingY={16}>
        <VStack spacing={4}>
          <Spinner size="lg" />
          <Text fontSize="lg" color="gray.700">
            {t("common:status.loadingApplication")}
          </Text>
        </VStack>
      </Center>
    );
  }

  const country =
    (applicationData?.applicant_type === "proxy"
      ? applicationData?.proxy_country
      : applicationData?.patient_country) || "US";

  return (
    <VStack spacing={6} alignItems="stretch" paddingBlockEnd={24}>
      <Flex fontSize="md" alignItems="baseline" gap={1} color="gray.600" fontWeight="semibold">
        <Link as={LocalizedLink} color="blue.500" to="/">
          &larr; {t("common:buttons.backToStepList")}
        </Link>
      </Flex>

      <VStack
        paddingBlock={[0, 4, 8]}
        spacing={8}
        maxWidth="container.md"
        margin="auto"
        alignItems="flex-start"
        marginBlockEnd={8}
      >
        <Box>
          <Heading fontSize={["2xl", "3xl", "4xl"]} fontWeight="regular">
            <Text
              fontSize="md"
              as="div"
              textTransform="uppercase"
              color="gray.600"
              fontWeight="semibold"
            >
              {t("registry:medical.stepLabel")}
            </Text>
            {t("registry:medical.heading")}
          </Heading>
          <Text fontSize="lg">{t("registry:medical.description")}</Text>
        </Box>

        <Stack spacing={10} width="full">
          {!canEditMedical && (
            <Alert
              status="info"
              fontSize="lg"
              padding={6}
              backgroundColor="gray.50"
              borderRadius="md"
            >
              <AlertIcon boxSize={6} marginInlineStart={2} marginInlineEnd={6} />
              <AlertDescription>
                <Trans
                  i18nKey="registry:medical.completeConsentFirst"
                  components={{
                    consentLink: (
                      <Link
                        as={LocalizedLink}
                        color="blue.500"
                        textDecoration="underline"
                        fontWeight="medium"
                        to="/registry/consent"
                      />
                    ),
                  }}
                />
              </AlertDescription>
            </Alert>
          )}

          <FieldController
            name="application.vision_conditions"
            control={formControl}
            disabled={!canEditMedical}
            render={({ field }) => (
              <Stack as="fieldset" gap={4}>
                <Text fontSize="lg" fontWeight="medium" as="legend">
                  {t(`registry:medical.visionConditionsLabel_${pronounKey}`)}{" "}
                  <Text as="span" role="presentation" color="red.600">
                    *
                  </Text>
                </Text>
                <Grid
                  templateColumns={["1fr", "1fr", "repeat(2, 1fr)"]}
                  gap={4}
                  marginBlockStart={2}
                >
                  {Object.keys(allVisionConditions).map((key) => {
                    const conditionKey =
                      allVisionConditions[key as keyof typeof allVisionConditions];
                    const isChecked = field.value?.includes(conditionKey);
                    return (
                      <InputCheckbox
                        key={conditionKey}
                        name={t(`registry:medical.conditions.${key}`)}
                        value={!!isChecked}
                        disabled={field.disabled}
                        onChange={(checked) => {
                          field.onChange(
                            checked
                              ? [...(field.value || []), conditionKey]
                              : field.value?.filter((c: string) => c !== conditionKey),
                          );
                        }}
                      />
                    );
                  })}
                </Grid>
              </Stack>
            )}
          />
          <Collapse in={hasMacularDegeneration}>
            <FieldController
              control={formControl}
              name={"application.vision_macular_degeneration_type"}
              disabled={!canEditMedical}
              render={({ field }) => (
                <RadioCards
                  {...field}
                  size="sm"
                  options={[
                    { label: t("registry:medical.macularWet"), value: "wet" },
                    { label: t("registry:medical.macularDry"), value: "dry" },
                    { label: t("registry:medical.macularBoth"), value: "both" },
                    { label: t("registry:medical.macularUnknown"), value: "unknown" },
                  ]}
                  value={field.value || undefined}
                  label={t(`registry:medical.macularDegenerationType_${pronounKey}`)}
                  isRequired={!!hasMacularDegeneration}
                />
              )}
            />
          </Collapse>

          <Collapse in={hasOtherVisionConditions}>
            <FieldController
              control={formControl}
              name={"application.vision_other_details"}
              disabled={!canEditMedical}
              label={t(`registry:medical.visionOtherDetails_${pronounKey}`)}
              labelProps={{ fontSize: "lg", marginBlockEnd: 2 }}
              render={({ field }) => (
                <InputTextArea
                  {...field}
                  value={field.value || undefined}
                  placeholder={t("registry:medical.visionOtherPlaceholder")}
                />
              )}
            />
          </Collapse>

          <FieldController
            control={formControl}
            name={"application.vision_acuity_known"}
            disabled={!canEditMedical}
            render={({ field }) => (
              <BoolRadioCards
                {...field}
                size="sm"
                label={t(`registry:medical.visualAcuityKnown_${pronounKey}`)}
                isRequired
              />
            )}
          />

          {(knowsVisualAcuity === true || knowsVisualAcuity === null) && (
            <Grid templateColumns="max-content max-content 1fr" gap={12} width="full">
              <FieldController
                control={formControl}
                name={"application.vision_acuity_left"}
                disabled={!canEditMedical}
                label={t("registry:medical.leftEye")}
                labelProps={{ fontSize: "lg", marginBlockEnd: 2 }}
                render={({ field }) => (
                  <VisualAccuity {...field} country={country} value={field.value ?? ""} />
                )}
              />
              <FieldController
                control={formControl}
                name={"application.vision_acuity_right"}
                disabled={!canEditMedical}
                label={t("registry:medical.rightEye")}
                labelProps={{ fontSize: "lg", marginBlockEnd: 2 }}
                render={({ field }) => (
                  <VisualAccuity {...field} country={country} value={field.value ?? ""} />
                )}
              />
            </Grid>
          )}

          {knowsVisualAcuity === false && (
            <Stack spacing={8} width="full">
              <FieldController
                control={formControl}
                name={"application.vision_can_read_normal_print"}
                disabled={!canEditMedical}
                render={({ field }) => (
                  <BoolRadioCards
                    {...field}
                    size="sm"
                    label={
                      <Trans
                        i18nKey={`registry:medical.canReadNormalPrint_${pronounKey}`}
                        components={{ strong: <strong /> }}
                      />
                    }
                    isRequired
                  />
                )}
              />
              <FieldController
                control={formControl}
                name={"application.vision_can_read_headlines"}
                disabled={!canEditMedical}
                render={({ field }) => (
                  <BoolRadioCards
                    {...field}
                    size="sm"
                    label={
                      <Trans
                        i18nKey={`registry:medical.canReadHeadlines_${pronounKey}`}
                        components={{ strong: <strong /> }}
                      />
                    }
                    isRequired
                  />
                )}
              />
              <FieldController
                control={formControl}
                name={"application.vision_can_see_indoors"}
                disabled={!canEditMedical}
                render={({ field }) => (
                  <BoolRadioCards
                    {...field}
                    size="sm"
                    label={t(`registry:medical.canSeeIndoors_${pronounKey}`)}
                    isRequired
                  />
                )}
              />
              <FieldController
                control={formControl}
                name={"application.vision_can_localize_person"}
                disabled={!canEditMedical}
                render={({ field }) => (
                  <BoolRadioCards
                    {...field}
                    size="sm"
                    label={t(`registry:medical.canLocalizePerson_${pronounKey}`)}
                    isRequired
                  />
                )}
              />
            </Stack>
          )}

          <FieldController
            control={formControl}
            name={"application.vision_driving"}
            disabled={!canEditMedical}
            render={({ field }) => (
              <RadioCards
                {...field}
                size="sm"
                options={[
                  {
                    label: t(`registry:medical.drivingYes_${pronounKey}`),
                    value: "yes",
                  },
                  {
                    label: t(`registry:medical.drivingLimited_${pronounKey}`),
                    value: "yes_with_limitations",
                  },
                  {
                    label: t(`registry:medical.drivingNoVision_${pronounKey}`),
                    value: "no_due_to_vision",
                  },
                  {
                    label: t(`registry:medical.drivingNoOther_${pronounKey}`),
                    value: "no_other",
                  },
                ]}
                value={field.value || undefined}
                label={t(`registry:medical.drivingLabel_${pronounKey}`)}
                isRequired
              />
            )}
          />

          {visionErrors.length > 0 && (
            <Box
              border="1px solid"
              borderColor="red.300"
              backgroundColor="red.50"
              fontSize="lg"
              as="ul"
              marginInlineStart={0}
              paddingBlock={4}
              paddingInline={6}
              borderRadius="lg"
              color="red.900"
              width="full"
            >
              {visionErrors.map((errorText) => (
                <Box listStyleType="none" as="li" paddingBlock={1} key={errorText}>
                  {errorText}
                </Box>
              ))}
            </Box>
          )}
        </Stack>
      </VStack>

      <Button
        data-testid="save-and-continue-button"
        colorScheme="blue"
        paddingInline={20}
        paddingY={8}
        fontSize="xl"
        width="full"
        maxWidth="container.md"
        marginInline="auto"
        onClick={handleContinue}
        isDisabled={!isHydrated || !medicalIsComplete || !canEditMedical}
      >
        {canEditMedical
          ? applicationSubmitted
            ? t("common:buttons.updateMedicalInfo")
            : t("common:buttons.saveAndContinue")
          : t("common:buttons.continue")}
      </Button>

      <ErrorSummary
        errors={medicalErrors}
        extraMessages={canEditMedical ? undefined : [t("validation:errorSummary.consentRequired")]}
      />
    </VStack>
  );
};
